import React from 'react';
import SectionHeader from '@/components/site/SectionHeader';
import GoldText from '@/components/site/GoldText';

const areas = [
  ['Time', 'Did the workflow require less manual effort?'],
  ['Quality', 'Were the outputs useful and sufficiently accurate for the task?'],
  ['Completion', 'Did required steps happen consistently?'],
  ['Oversight', 'How much review or intervention remained necessary?'],
  ['Cost', 'What did the workflow cost to operate?'],
  ['Adoption', 'Could the team use it effectively?'],
];

const evaluationAreas = [
  'Response preparation time',
  'Required information captured',
  'Output quality',
  'Human intervention',
  'Operating cost',
];

/** Report preview shows structure only — no invented performance figures. */
const ReportPreview = () => (
  <figure aria-label="Sample pilot review structure" className="surface surface-lit overflow-hidden">
    <div className="flex items-end justify-between border-b border-rule px-6 py-5">
      <div>
        <p className="eyebrow">Pilot review</p>
        <p className="mt-1 font-serif text-[1.4rem] leading-tight">Sample structure</p>
      </div>
      <span className="meta-label">No figures shown</span>
    </div>
    <div className="space-y-6 p-5 sm:p-6">
      <div>
        <p className="meta-label mb-1">Workflow</p>
        <p className="font-medium">Customer inquiry handling</p>
      </div>
      <div>
        <p className="meta-label mb-2">Evaluation areas</p>
        <ul className="border-y border-rule font-mono text-xs">
          {evaluationAreas.map((a) => (
            <li
              key={a}
              className="flex items-center justify-between gap-4 border-b border-dashed border-rule py-2.5 last:border-b-0"
            >
              <span>{a}</span>
              <span className="text-mist">baseline → pilot</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="meta-label mb-2">Decision</p>
        <div className="flex gap-2">
          {['Continue', 'Revise', 'Stop'].map((d) => (
            <span
              key={d}
              className="border border-rule2 px-3 py-1.5 font-mono text-[0.7rem] uppercase tracking-[0.12em]"
            >
              {d}
            </span>
          ))}
        </div>
      </div>
    </div>
  </figure>
);

const Measurement = () => (
  <section aria-labelledby="measure-title" className="border-t border-rule">
    <div className="section-container grid gap-12 lg:grid-cols-2">
      <div>
        <SectionHeader
          id="measure-title"
          eyebrow="06 / Measurement"
          title={
            <>
              Define “better” <GoldText>before you build.</GoldText>
            </>
          }
        >
          <p>We agree on what a useful result would look like, then evaluate the pilot against that definition.</p>
        </SectionHeader>
        <dl className="reveal mt-10 border-t border-rule">
          {areas.map(([area, q]) => (
            <div
              key={area}
              className="grid grid-cols-[auto_6.5rem_1fr] items-baseline gap-3.5 border-b border-rule py-3.5"
            >
              <span aria-hidden="true" className="font-mono text-sm text-goldcore">
                +
              </span>
              <dt className="font-medium">{area}</dt>
              <dd className="text-sm text-mist">{q}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="reveal flex flex-col gap-6">
        <ReportPreview />
        <p className="text-sm text-mist">
          Sample artifact. Case studies will be added as completed pilots produce usable evidence—each explaining the
          original process, scope, measurement period, observed results, and limitations.
        </p>
      </div>
    </div>
  </section>
);

export default Measurement;
